import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layers, MessageSquare, PanelLeftOpen } from 'lucide-react';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { useEditorContext } from './context';
import type { Chapter } from '../../types';
import { chapterNumberOf, railNav, railNumberLabel } from './sidebarRailNav';
import './styles/sidebarRail.css';

/**
 * 侧栏收起后的**窄栏**：收起不等于"没有导航"。
 *
 * 收起态此前只有一个「展开侧栏」按钮 —— 手机上想跳到第 30 章得先把侧栏展开、
 * 再从章节树里找，收起反而是给自己上锁。现在窄栏里直接给出**每个分卷一枚按钮**。

 * 布局（`[展开侧栏]` 固定在最上，下面是可滚动区）：
 * ```
 * [Layers 1]   ← 每个分卷各一枚按钮（有几个卷就有几枚）
 *   1 2 3 …    ← 该卷的章节号列表（点卷按钮展开/收起）
 * [Layers 2]
 *   1
 * ```
 *
 * * **每个分卷都有自己的按钮**。早先只有一枚"当前卷"按钮、列表里也只列当前卷的章节，
 *   于是新增分卷后当前章落到新卷，就再也回不到别的卷 —— 收起侧栏本该"仍可导航"，
 *   却成了单卷死胡同（用户实测报出来的）。现在点任意一枚卷按钮就展开**那一卷**的章节。
 * * 卷按钮显示**卷序号**；当前章节所在的那一卷额外高亮（`is-current`），
 *   所以"我在哪"一眼可读。
 * * 多个卷可同时展开（与侧栏章节树一致：各卷独立展开/收起），再点一次收起该卷。
 * * 章号按升序；点章号直接跳转（`handleSelectChapter` 内部会 `setActiveView('chapters')`，
 *   所以从别的视图点章号也能切回正文）。
 * * **刻意不放「AI 对话」入口**：AI 对话视图在没选中会话时只显示空态提示，
 *   而"新建对话 / 历史会话"两个入口都在侧栏里 —— 侧栏收着时点进去只会卡在空态，
 *   既无历史可选、也没有输入框。要加回来得先让那个视图自己能在空态下新建对话。
 * * 数据全部来自 `useWorkspace()`（卷 / 章节 / 选中章节），不新增任何取数接口。
 */
export function SidebarRail({ onExpand }: { onExpand: () => void }): React.ReactElement {
  const { volumes, chaptersMap, selectedChapter, handleSelectChapter, numberToChinese } = useWorkspace();
  const { activeView, setActiveView } = useEditorContext();

  const nav = useMemo(
    () => railNav(volumes, chaptersMap, selectedChapter),
    [volumes, chaptersMap, selectedChapter],
  );

  /**
   * 当前展开了哪些卷（存卷 id）。
   *
   * 允许多个同时展开 —— 与侧栏章节树的行为一致（各卷独立展开/收起），
   * 也避免"点开第二卷却把第一卷的列表关掉"这种把信息藏起来的意外。
   */
  const [openVolumeIds, setOpenVolumeIds] = useState<string[]>([]);

  // 卷被删掉后把它从展开集合里剔掉，免得留下永远匹配不上的死 id
  useEffect(() => {
    setOpenVolumeIds(prev => {
      const valid = prev.filter(id => nav.groups.some(g => g.volumeId === id));
      return valid.length === prev.length ? prev : valid;
    });
  }, [nav.groups]);

  const toggleVolume = useCallback((volumeId: string) => {
    setOpenVolumeIds(prev => (
      prev.includes(volumeId) ? prev.filter(id => id !== volumeId) : [...prev, volumeId]
    ));
  }, []);

  // 展开后把**当前章**滚进视野：一本几百章的书，列表初始位置通常在卷首，
  // 而用户想看的是"我现在在哪一章的上下文里"。
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const currentChapterRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const box = scrollRef.current;
    const el = currentChapterRef.current;
    if (!box || !el) return;   // 当前章所在那一卷没展开 → 没有可滚的目标
    // 只改这个滚动容器的 scrollTop，**不用 scrollIntoView** —— 那个会把所有可滚祖先
    // 一起滚（窄栏常驻，外层页面不该因为展开列表而跳一下）。
    const boxBox = box.getBoundingClientRect();
    const elBox = el.getBoundingClientRect();
    const outOfView = elBox.top < boxBox.top || elBox.bottom > boxBox.bottom;
    if (outOfView) {
      box.scrollTop += elBox.top - boxBox.top - Math.max(0, (box.clientHeight - elBox.height) / 2);
    }
  }, [openVolumeIds]);

  /**
   * 跳章。`handleSelectChapter` 是 async（内部可能弹"有未保存改动"的确认框），
   * 窄栏不等它 —— 失败原因由 Context 自己提示，这里只把 rejection 吞掉，
   * 免得留下未处理的 Promise。
   */
  const jumpTo = useCallback((chapter: Chapter) => {
    handleSelectChapter(chapter).catch(() => { /* 提示由 WorkspaceContext 负责 */ });
  }, [handleSelectChapter]);

  /**
   * 列表里某个章节按钮的两个文本。
   * `aria` 用阿拉伯数字 —— 与按钮里**可见**的那个数字一致（可访问名要包含可见标签，
   * 否则"用语音说可见文字"点不到它）；`title` 用中文数字 —— 与章节树/卷头的
   * "第一章 xxx"写法一致，鼠标悬停时可读。
   */
  const chapterLabels = (chapter: Chapter): { aria: string; title: string } => {
    const n = chapterNumberOf(chapter);
    const name = chapter.title || '未命名章节';
    if (n <= 0) return { aria: name, title: name };
    return { aria: `第${n}章 ${name}`, title: `第${numberToChinese(n)}章 ${name}` };
  };

  return (
    <nav className="sidebar-rail" aria-label="章节目录">
      <button
        type="button"
        className="sidebar-rail-btn"
        onClick={onExpand}
        title="展开侧栏"
        aria-label="展开侧栏"
      >
        <PanelLeftOpen size={18} />
      </button>

      {/* 「AI 对话」入口（**开关式**）。
          收起态的一级入口只剩章节树与会话列表：章节树由下面的卷/章按钮承担，
          AI 对话则**没有任何入口**（原先挡住它的死路 —— 空态里点不到"新建对话" ——
          已经修掉：会话列表连同「新建对话」都搬进视图内部了，见 `AIChatContent`）。

          已经是 AI 对话视图时再点一次 → **回到正在编辑的章节**（用户要求）。
          切回章节不会中断 AI 生成：该视图在 `Editor/index.tsx` 里是"打开过就常驻、
          只切 hidden"，组件不卸载。

          固定在滚动区**之上**，不随下面的卷/章列表滚走。 */}
      <button
        type="button"
        className={`sidebar-rail-nav${activeView === 'ai-chat' ? ' is-active' : ''}`}
        onClick={() => setActiveView(activeView === 'ai-chat' ? 'chapters' : 'ai-chat')}
        aria-label={activeView === 'ai-chat' ? '返回章节' : 'AI 对话'}
        aria-pressed={activeView === 'ai-chat'}
        title={activeView === 'ai-chat' ? '返回章节（AI 生成不中断）' : 'AI 对话'}
      >
        <MessageSquare size={18} />
      </button>

      <div className="sidebar-rail-scroll" ref={scrollRef}>
        {nav.groups.map((group) => {
          const open = openVolumeIds.includes(group.volumeId);
          const listId = `sidebar-rail-chap-list-${group.volumeId}`;
          const isCurrentVolume = group.volumeId === nav.volumeId;
          const volTitle = `第${numberToChinese(group.volumeIndex)}卷 ${group.volumeTitle}`
            + ` —— 点击${open ? '收起' : '展开'}本卷章节列表`;
          return (
            <div key={group.volumeId} className="sidebar-rail-vol-block">
              {/* 每个分卷一枚按钮：Layers 图标 + 卷序号 */}
              <button
                type="button"
                className={`sidebar-rail-vol${isCurrentVolume ? ' is-current' : ''}`}
                onClick={() => toggleVolume(group.volumeId)}
                aria-label={`第${group.volumeIndex}卷 ${group.volumeTitle}`}
                aria-expanded={open}
                aria-controls={listId}
                title={volTitle}
              >
                <Layers size={16} aria-hidden="true" />
                <span className="sidebar-rail-num">{railNumberLabel(group.volumeIndex)}</span>
              </button>

              {open && (
                <div className="sidebar-rail-chap-list" id={listId}>
                  {group.chapters.map((chapter) => {
                    const n = chapterNumberOf(chapter);
                    const isCurrent = selectedChapter?.id === chapter.id;
                    const labels = chapterLabels(chapter);
                    return (
                      <button
                        key={chapter.id}
                        type="button"
                        ref={isCurrent ? currentChapterRef : undefined}
                        className={`sidebar-rail-chap-item${isCurrent ? ' is-current' : ''}`}
                        onClick={() => jumpTo(chapter)}
                        title={labels.title}
                        aria-label={labels.aria}
                        aria-current={isCurrent ? 'true' : undefined}
                      >
                        {railNumberLabel(n)}
                      </button>
                    );
                  })}
                  {group.chapters.length === 0 && (
                    <div className="sidebar-rail-chap-empty" title="本卷暂无章节">—</div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {nav.groups.length === 0 && (
          <div className="sidebar-rail-chap-empty" title="本书还没有分卷">—</div>
        )}
      </div>
    </nav>
  );
}
